import { useCallback } from "react";
import { logLandingEvent } from "../helpers/AnalyticsTags";

export default function useLandingAnalytics() {
  const trackEvent = useCallback(({ event, section = "", label = "", metadata = {} }) => {
    logLandingEvent({ event, section, label, metadata });
  }, []);

  const trackNavClick = useCallback(
    (label, href, surface) => {
      trackEvent({
        event: "landing_nav_click",
        section: "header",
        label,
        metadata: { target: href, surface },
      });
    },
    [trackEvent]
  );

  const trackContactCTA = useCallback(
    (surface, section = "header") => {
      trackEvent({
        event: "landing_contact_cta",
        section,
        label: "Contact Us",
        metadata: { surface },
      });
    },
    [trackEvent]
  );

  const trackMenuToggle = useCallback(
    (isOpen) => {
      trackEvent({
        event: "landing_nav_menu_toggle",
        section: "header",
        label: isOpen ? "open" : "close",
      });
    },
    [trackEvent]
  );

  const trackHeroCTA = useCallback(
    (label, destination) => {
      trackEvent({
        event: "landing_hero_cta_click",
        section: "hero",
        label,
        metadata: { destination },
      });
    },
    [trackEvent]
  );

  const trackSolutionCTA = useCallback(
    (action, label) => {
      trackEvent({
        event: `landing_solution_${action}`,
        section: "solutions",
        label,
      });
    },
    [trackEvent]
  );

  const trackContactSubmitStart = useCallback(
    (organizationType) => {
      trackEvent({
        event: "landing_contact_submit",
        section: "contact_form",
        label: "Contact Us",
        metadata: { organizationType: organizationType || "unspecified" },
      });
    },
    [trackEvent]
  );

  const trackContactSubmitSuccess = useCallback(
    (organizationType) => {
      trackEvent({
        event: "landing_contact_submit_success",
        section: "contact_form",
        label: "Contact Us",
        metadata: { organizationType: organizationType || "unspecified" },
      });
    },
    [trackEvent]
  );

  const trackContactSubmitError = useCallback(
    (organizationType, message = "unknown_error") => {
      trackEvent({
        event: "landing_contact_submit_error",
        section: "contact_form",
        label: "Contact Us",
        metadata: {
          organizationType: organizationType || "unspecified",
          message,
        },
      });
    },
    [trackEvent]
  );

  const createExternalLinkHandler = useCallback(
    ({ eventName, section, label, url, metadata = {} }) => (event) => {
      if (event?.preventDefault) {
        event.preventDefault();
      }

      trackEvent({
        event: eventName,
        section,
        label,
        metadata: { url, ...metadata },
      });

      if (typeof window !== "undefined") {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [trackEvent]
  );

  return {
    trackNavClick,
    trackContactCTA,
    trackMenuToggle,
    trackHeroCTA,
    trackSolutionCTA,
    trackContactSubmitStart,
    trackContactSubmitSuccess,
    trackContactSubmitError,
    createExternalLinkHandler,
  };
}
